import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CreditCard, Heart, Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const COUNTRIES = [["GB", "United Kingdom"], ["US", "United States"], ["IN", "India"],
                   ["AE", "United Arab Emirates"], ["KE", "Kenya"], ["OTHER", "Somewhere else"]];

const F = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] text-muted-foreground/80">{hint}</p>}
  </div>
);

export default function PublicGive() {
  const { campaign } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    amount: 0, name: "", email: "", phone: "", country: "GB", fund_code: "",
    programme_code: "", cover_fee: false, anonymous: false, declaration: false,
    title: "", first_name: "", last_name: "", address_line: "", postcode: "", tax_id: "",
  });

  const load = useCallback(async () => {
    const res = await api.get("/public/give/config");
    setCfg(res.data);
    setForm((f) => ({ ...f, amount: (res.data.presets || []).find((p) => p >= res.data.min_gift)
                      || res.data.min_gift,
                      fund_code: res.data.funds?.[0]?.code || "" }));
  }, []);

  useEffect(() => {
    load().catch(() => setCfg(false));
    if (params.get("cancelled")) toast.message("Nothing was charged — you can try again any time.");
  }, [load, params]);

  const fee = cfg && form.cover_fee
    ? Math.round((form.amount * (cfg.fee_percent || 0) / 100 + (cfg.fee_fixed || 0)) * 100) / 100
    : 0;

  const give = async () => {
    setBusy(true);
    try {
      const res = await api.post("/public/give/checkout", {
        ...form, campaign: campaign || params.get("campaign") || "",
        source_channel: params.get("src") || "", origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : apiError(e));
      setBusy(false);
    }
  };

  if (cfg === null) {
    return <div className="grid min-h-screen place-items-center bg-background">
      <Loader2 className="h-5 w-5 animate-spin text-primary" />
    </div>;
  }
  if (cfg === false) {
    return <div className="grid min-h-screen place-items-center bg-background px-4">
      <div className="panel max-w-md p-8 text-center" data-testid="give-closed">
        <h1 className="text-base font-semibold text-foreground md:text-lg">
          Online giving is closed just now
        </h1>
        <p className="mt-2 text-[12.5px] text-muted-foreground">
          Please contact the charity directly and they will take your gift another way.
        </p>
      </div>
    </div>;
  }

  const declaration = cfg.declarations?.[form.country];
  const claimField = (key) => (cfg.claim_fields || []).find((f) => f.key === key)?.mode || "off";
  const shows = (key) => claimField(key) !== "off";
  const needs = (key) => claimField(key) === "mandatory";
  const claimMissing = !form.anonymous && [
    needs("name_parts") && !(form.first_name.trim() && form.last_name.trim()),
    needs("address_line") && !form.address_line.trim(),
    needs("postcode") && !form.postcode.trim(),
    needs("tax_id") && !form.tax_id.trim(),
  ].some(Boolean);

  return (
    <div className="min-h-screen bg-background" data-testid="give-page">
      <header className="border-b border-border bg-card/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-2.5 px-4 py-3">
          {cfg.logo_url
            ? <img src={cfg.logo_url} alt="" className="h-8 w-8 rounded-[9px] object-cover" />
            : <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-gradient-to-br from-primary to-[hsl(var(--teal))]">
                <Heart className="h-3.5 w-3.5 text-white" />
              </span>}
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-foreground">{cfg.organisation}</div>
            <div className="text-[10.5px] uppercase tracking-wide text-muted-foreground">Secure giving</div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/donor/signin")}
                  data-testid="give-signin-link"
                  className="ml-auto rounded-full text-[11.5px] text-muted-foreground">
            Already a supporter?
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 px-4 py-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            {cfg.headline}
          </h1>
          <p className="mt-3 max-w-2xl text-[13px] text-muted-foreground">{cfg.blurb}</p>
        </div>

        {(cfg.impact_stats || []).length > 0 && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="give-impact">
            {cfg.impact_stats.slice(0, 3).map((s) => (
              <div key={s.label} className="panel p-4">
                <div className="text-lg font-semibold text-foreground">{s.value}</div>
                <div className="text-[11px] text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="panel p-6">
          {!cfg.configured && (
            <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-[11.5px] text-amber-700 dark:text-amber-300"
                 data-testid="give-unavailable">
              Card payments are not switched on for this charity yet. Please get in touch and they
              will arrange another way to give.
            </div>
          )}

          <div className="flex flex-wrap gap-2" data-testid="give-presets">
            {(cfg.presets || []).filter((p) => p >= cfg.min_gift && p <= cfg.max_gift).map((p) => (
              <button key={p} type="button" onClick={() => setForm({ ...form, amount: p })}
                      data-testid={`give-preset-${p}`}
                      className={cn("rounded-full border px-4 py-1.5 text-[12.5px] transition-colors",
                        form.amount === p
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border text-foreground hover:border-primary/60")}>
                {cfg.currency} {p.toLocaleString()}
              </button>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <F label={`Amount (${cfg.currency})`}>
              <Input type="number" value={form.amount} data-testid="give-amount"
                     onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
                     className="bg-muted/40 font-mono" />
            </F>
            <F label="Towards">
              <Select value={form.fund_code} onValueChange={(v) => setForm({ ...form, fund_code: v })}>
                <SelectTrigger data-testid="give-fund" className="bg-muted/40"><SelectValue /></SelectTrigger>
                <SelectContent>{(cfg.funds || []).map((f) => (
                  <SelectItem key={f.code} value={f.code}>{f.name}</SelectItem>))}</SelectContent>
              </Select>
            </F>
            <F label="Programme (optional)">
              <Select value={form.programme_code}
                      onValueChange={(v) => setForm({ ...form, programme_code: v })}>
                <SelectTrigger data-testid="give-programme" className="bg-muted/40">
                  <SelectValue placeholder="Wherever needed most" /></SelectTrigger>
                <SelectContent>{(cfg.programmes || []).map((p) => (
                  <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>))}</SelectContent>
              </Select>
            </F>

            {!form.anonymous && (
              <>
                <F label="Your name">
                  <Input value={form.name} data-testid="give-name"
                         onChange={(e) => setForm({ ...form, name: e.target.value })}
                         className="bg-muted/40" />
                </F>
                <F label="Email (your receipt goes here)">
                  <Input value={form.email} data-testid="give-email"
                         onChange={(e) => setForm({ ...form, email: e.target.value })}
                         className="bg-muted/40 font-mono text-[12px]" />
                </F>
                <F label="Country of residence">
                  <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                    <SelectTrigger data-testid="give-country" className="bg-muted/40"><SelectValue /></SelectTrigger>
                    <SelectContent>{COUNTRIES.map(([code, name]) => (
                      <SelectItem key={code} value={code}>{name}</SelectItem>))}</SelectContent>
                  </Select>
                </F>
              </>
            )}
          </div>

          {!form.anonymous && (shows("name_parts") || shows("address_line")
            || shows("postcode") || shows("tax_id")) && (
            <div className="mt-5 rounded-xl border border-border bg-muted/20 p-4"
                 data-testid="give-claim-block">
              <div className="kicker mb-1">For your tax receipt and the charity's claim</div>
              <p className="mb-3 text-[11px] text-muted-foreground">
                The tax authority needs these to accept a Gift Aid or 80G claim on your gift.
                {" "}Anything not marked required can be left blank.
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {shows("name_parts") && (
                  <>
                    <F label={`First name${needs("name_parts") ? " (required)" : ""}`}>
                      <Input value={form.first_name} data-testid="give-first-name"
                             onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                             className="bg-muted/40" />
                    </F>
                    <F label={`Last name${needs("name_parts") ? " (required)" : ""}`}>
                      <Input value={form.last_name} data-testid="give-last-name"
                             onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                             className="bg-muted/40" />
                    </F>
                  </>
                )}
                {shows("address_line") && (
                  <F label={`House name or number and street${needs("address_line") ? " (required)" : ""}`}>
                    <Input value={form.address_line} data-testid="give-address"
                           onChange={(e) => setForm({ ...form, address_line: e.target.value })}
                           className="bg-muted/40" />
                  </F>
                )}
                {shows("postcode") && (
                  <F label={`Postcode${needs("postcode") ? " (required)" : ""}`}
                     hint="Needed for a UK Gift Aid claim.">
                    <Input value={form.postcode} data-testid="give-postcode"
                           onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                           className="bg-muted/40 font-mono text-[12px]" />
                  </F>
                )}
                {shows("tax_id") && (
                  <F label={`PAN or tax reference${needs("tax_id") ? " (required)" : ""}`}
                     hint="Needed on Form 10BD for an Indian 80G claim.">
                    <Input value={form.tax_id} data-testid="give-tax-id"
                           onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                           className="bg-muted/40 font-mono text-[12px]" />
                  </F>
                )}
              </div>
            </div>
          )}

          <div className="mt-5 space-y-2.5">
            <label className="flex items-start gap-2 text-[12px] text-foreground">
              <Checkbox checked={form.cover_fee} data-testid="give-cover-fee"
                        onCheckedChange={(v) => setForm({ ...form, cover_fee: Boolean(v) })} />
              <span>
                Add {cfg.currency} {fee.toLocaleString()} to cover the card fee, so the full
                amount reaches the work
              </span>
            </label>
            {!form.anonymous && declaration && (
              <label className="flex items-start gap-2 text-[12px] text-foreground">
                <Checkbox checked={form.declaration} data-testid="give-declaration"
                          onCheckedChange={(v) => setForm({ ...form, declaration: Boolean(v) })} />
                <span>{declaration}</span>
              </label>
            )}
            {cfg.allow_anonymous && (
              <label className="flex items-start gap-2 text-[12px] text-muted-foreground">
                <Checkbox checked={form.anonymous} data-testid="give-anonymous"
                          onCheckedChange={(v) => setForm({ ...form, anonymous: Boolean(v) })} />
                <span>Give anonymously — no name, no email, and no receipt can be issued</span>
              </label>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button onClick={give} disabled={busy || !cfg.configured || !form.amount || claimMissing}
                    data-testid="give-submit"
                    className="rounded-full bg-primary px-6 text-primary-foreground hover:bg-primary/90">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <CreditCard className="mr-2 h-4 w-4" />}
              Give {cfg.currency} {(form.amount + fee).toLocaleString()}
            </Button>
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Lock className="h-3 w-3" /> {cfg.provider} takes the payment on their own secure page
            </span>
          </div>
        </div>

        <p className="flex items-start gap-2 text-[11.5px] text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          {cfg.impact_statement}
        </p>
      </main>
    </div>
  );
}
